import React from 'react';
import Modal from '../../commonComponents/util/Modal';
import NavigationComponent from '../../commonComponents/util/NavigationComponent';
import ApproverReplacementModal from './ApproverReplacementModal';
import LoadingSpinner from '../../commonComponents/util/LoadingSpinner';
import ValidatedInput from '../../commonComponents/util/ValidatedInput';
import Select from '../../commonComponents/util/Select';
import MultiSelect from '../../commonComponents/util/MultiSelect';
import RadioGroup from '../../commonComponents/util/RadioGroup';
import ValidatedPhoneInput from '../../commonComponents/util/ValidatedPhoneInput';
import FadeOutErrorMessage from '../../commonComponents/util/FadeOutErrorMessage';
import { isValidEmail, deepEquals, formatCurrency, toTitleCase } from '../../../lib/utils';
import { Departments, PermissionTypes } from '../../../lib/systemVariables';
import { PhoneNumberTypes } from '../../../lib/constants';
import _cloneDeep from 'lodash.clonedeep';

export default class UsersSettingsModal extends NavigationComponent {
    constructor(props) {
        super(props);

        this.initialState = {
            userId: '',
            preEditUserDetails: {},
            userDetails: {
                firstName: '',
                lastName: '',
                phoneNumber: '',
                phoneType: PhoneNumberTypes.American,
                mobilePhone: '',
                mobilePhoneType: PhoneNumberTypes.American,
                email: '',
                userName: '',
                defaultApproverId: '',
                defaultPrinterId: '',
                departments: [Departments.US],
                roleId: '',
                limitedFunds: []
            },
            preEditPermissionsForRole: [],
            preEditAdditionalPermissions: this.getDefaultAdditionalPermissions(true),
            additionalPermissions: this.getDefaultAdditionalPermissions(true),
            replacementApproverId: '',
            submitAttempted: false,
            invalidData: {},
            error: false,
            showModals: {
                "approverReplacementModal": false
            }
        };

        this.state = _cloneDeep(this.initialState);

        this.onChange = this.onChange.bind(this);
        this.onTogglePermission = this.onTogglePermission.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.editUserId && nextProps.show && !this.props.show) {
            this.getUserDetails(nextProps.editUserId);
        }
    }

    recipientTypePermissions = {
        Individuals: "IndividualsManage",
        Institutions: "InstitutionsManage"
    };

    async getUserDetails(editUserId) {
        await Promise.all([
            this.props.getUserDetails(editUserId),
            this.props.getUserPermissions(editUserId)
        ]);

        const { userId, ...userDetails } = this.props.userDetails.data;
        userDetails.mobilePhoneType = userDetails.departments && userDetails.departments.includes(Departments.US) ? PhoneNumberTypes.American : PhoneNumberTypes.Other;
        userDetails.phoneType = userDetails.departments && userDetails.departments.includes(Departments.US) ? PhoneNumberTypes.American : PhoneNumberTypes.Other;
        this.setState({
            userId,
            preEditUserDetails: { ...this.state.userDetails, ...userDetails },
            userDetails: { ...this.state.userDetails, ...userDetails }
        });

        if (userDetails.roleId) {
            await this.props.getPermissionsForRole(userDetails.roleId);
            const additionalPermissions = this.props.userPermissions.data.filter(permission => this.props.permissionsForRole.data.indexOf(permission) < 0);
            this.setState({
                preEditPermissionsForRole: this.props.userPermissions.data,
                preEditAdditionalPermissions: additionalPermissions,
                additionalPermissions: additionalPermissions
            });
        }
    }

    onChange(name, value) {
        if (name === "firstName" || name === "lastName") value = toTitleCase(value);
        this.setState({
            userDetails: { ...this.state.userDetails, ...{ [name]: value } }
        }, () => {
            if (name === "email") {
                this.setState({
                    userDetails: { ...this.state.userDetails, ...{ userName: value } }
                });
            } else if (name === "roleId") {
                this.props.getPermissionsForRole(value);
                this.setState({ additionalPermissions: this.getDefaultAdditionalPermissions() });
            }
            else if (name === "departments" && value.length === 1) {
                let updatedDefaultApproverId = this.state.userDetails.defaultApproverId;
                let updatedLimitedFunds = this.state.userDetails.limitedFunds;

                if (this.state.userDetails.defaultApproverId) {
                    const defaultApprover = this.props.approvers[this.props.approvers.map(a => a.key.toString())
                        .indexOf(this.state.userDetails.defaultApproverId.toString())];
                    if (defaultApprover && defaultApprover.departments.indexOf(value[0]) < 0) {
                        updatedDefaultApproverId = "";
                    }
                }

                if (this.state.additionalPermissions.indexOf(PermissionTypes.LimitedFundsAccess) >= 0 && this.state.userDetails.limitedFunds.length) {
                    const accessibleFundIds = [
                        ...this.state.userDetails.departments.indexOf(Departments.US) >= 0
                            ? this.props.usFunds
                            : [],
                        ...this.state.userDetails.departments.indexOf(Departments.IL) >= 0
                            ? this.props.israelFunds
                            : [],
                    ].map(f => f.key);
                    updatedLimitedFunds = this.state.userDetails.limitedFunds.filter(fundId => accessibleFundIds.indexOf(fundId) >= 0);
                }

                this.setState({
                    userDetails: {
                        ...this.state.userDetails,
                        defaultApproverId: updatedDefaultApproverId,
                        limitedFunds: updatedLimitedFunds
                    }
                });
            }
        });
    }

    onResetPermissions = () => {
        this.setState({
            additionalPermissions: this.getDefaultAdditionalPermissions()
        });
    }

    getDefaultAdditionalPermissions = (noRole = false) => {
        const userRole = noRole ? null : this.props.roles.find(role => role.key.toString() === this.state.userDetails.roleId.toString());
        const defaultRecipientTypePermissions = (userRole && userRole.value === "Account Manager")
            ? [this.recipientTypePermissions.Individuals]
            : [this.recipientTypePermissions.Individuals, this.recipientTypePermissions.Institutions];
        return [
            PermissionTypes.AllFundsAccess,
            ...defaultRecipientTypePermissions
        ];
    }

    onTogglePermission(event) {
        this.setState({
            additionalPermissions: event.target.checked ? [...this.state.additionalPermissions, event.target.name] : this.state.additionalPermissions.filter(p => p !== event.target.name)
        });
    }

    onToggleRecipientTypePermission = (permission) => {
        const recipientTypePermissions = Object.keys(this.recipientTypePermissions).map(key => this.recipientTypePermissions[key]);
        let additionalPermissions = this.state.additionalPermissions.filter(p => recipientTypePermissions.indexOf(p) < 0);

        if (permission === "all") {
            additionalPermissions.push(...recipientTypePermissions);
        } else {
            additionalPermissions.push(permission);
        }

        this.setState({ additionalPermissions });
    }

    onToggleFundsPermission = (fundPermission) => {
        let additionalPermissions = [
            ...this.state.additionalPermissions
                .filter(p => p !== PermissionTypes.AllFundsAccess && p !== PermissionTypes.LimitedFundsAccess),
            fundPermission
        ];

        let limitedFunds = fundPermission === PermissionTypes.AllFundsAccess ? [] : this.state.userDetails.limitedFunds;

        this.setState({
            additionalPermissions,
            userDetails: {
                ...this.state.userDetails,
                limitedFunds
            }
        });
    }

    onSelectLimitedFunds = (selectedFunds, allAccessibleFunds) => {
        const limitedFunds = (
            selectedFunds.map(f => f.value).indexOf("all") >= 0
                ? allAccessibleFunds
                : selectedFunds
        ).map(f => f.value);

        this.setState({
            userDetails: {
                ...this.state.userDetails,
                limitedFunds
            }
        });
    }

    async onSave(ignoreApprover) {
        this.setState({ error: false, submitAttempted: true });

        const userDetails = this.state.userDetails;
        if (this.state.invalidData.phoneNumber || this.state.invalidData.mobilePhone || this.state.invalidData.email ||
            !userDetails.firstName || !userDetails.lastName || !userDetails.phoneNumber || !userDetails.mobilePhone ||
            !userDetails.email || !userDetails.userName || !userDetails.defaultApproverId || !userDetails.departments.length || !userDetails.roleId) {
            return;
        }

        if (this.props.editUserId && !ignoreApprover) {
            const isApprover = (
                this.state.preEditPermissionsForRole.indexOf(PermissionTypes.ApplicationsApproveFull) >= 0
                || this.state.preEditPermissionsForRole.indexOf(PermissionTypes.ApplicationsApproveLimited) >= 0
                || this.state.preEditAdditionalPermissions.indexOf(PermissionTypes.ApplicationsApproveFull) >= 0
                || this.state.preEditAdditionalPermissions.indexOf(PermissionTypes.ApplicationsApproveLimited) >= 0
            );
            if (isApprover) {
                if (this.props.permissionsForRole.data.indexOf(PermissionTypes.ApplicationsApproveFull) < 0
                    && this.props.permissionsForRole.data.indexOf(PermissionTypes.ApplicationsApproveLimited) < 0
                    && this.state.additionalPermissions.indexOf(PermissionTypes.ApplicationsApproveFull) < 0
                    && this.state.additionalPermissions.indexOf(PermissionTypes.ApplicationsApproveLimited) < 0) {
                    this.toggleModal("approverReplacementModal");
                    return;
                }
            }
        }

        const update = { ...this.state.userDetails, additionalPermissions: this.state.additionalPermissions };
        this.props.editUserId ? await this.props.updateUser(this.state.userId, update, this.state.replacementApproverId) : await this.props.createUser(update);

        if (this.props.editUserId ? this.props.updatingUser.success : this.props.creatingUser.success) {
            this.onClose();
            this.props.reloadUsers();
        } else {
            this.setState({ error: true });
        }
    }

    onClose() {
        this.props.close();
        this.setState(this.initialState);
    }

    validatePhone(name, value) {
        this.setState({ invalidData: { ...this.state.invalidData, [name]: value } });
    }

    validateEmail(email) {
        const isValid = isValidEmail(email);
        this.setState({ invalidData: { ...this.state.invalidData, email: !isValid } });
        return isValid;
    }

    render() {
        let permissionsByCategory = {};
        const permissionCategories = [
            { key: "Applications", value: "Applications" },
            { key: "RecipientAccnts", value: "Recipients" },
            { key: "CampaignAndLists", value: "Campaigns and Lists" },
            { key: "Payments", value: "Payments" },
            { key: "Accounting", value: "Accounting" },
            { key: "Reporting", value: "Reporting" },
            { key: "UsersAndSettings", value: "Users and Settings" }
        ];
        permissionCategories.forEach(category => {
            permissionsByCategory[category.value] = [];
            this.props.permissionTypes.forEach(permission => {
                if (permission.key.indexOf(category.key) === 0) {
                    const permissionByCateg = { ...permission, displayValue: permission.value.replace(`${category.value} `, '') };
                    permissionsByCategory[category.value].push(permissionByCateg);
                }
            });
        });

        const accessibleFunds = [
            ...this.state.userDetails.departments.indexOf(Departments.US) >= 0
                ? this.props.usFunds
                : [],
            ...this.state.userDetails.departments.indexOf(Departments.IL) >= 0
                ? this.props.israelFunds
                : [],
        ].map((fund) => ({
            label: fund.value,
            value: fund.key
        }));

        return (
            <div>
                <Modal modalShowing={this.props.show}>
                    <div className="modal-container">
                        <div className="modal settings-modal xl-modal">
                            {(!this.props.userDetails.loading && !this.props.userPermissions.loading
                                && (!this.props.userDetails.success || !this.props.userPermissions.success)) &&
                                <span className="error-label">Something went wrong and we were unable to retrieve user account settings.  Please try again.</span>}

                            <div className="bordered-modal-header">
                                <h4 className="modal-page-header">{this.props.editUserId ? 'User Account Settings' : 'New User Account'}</h4>
                                <i className="icon ion-ios-close-empty" onClick={this.onClose.bind(this)}></i>
                            </div>

                            {(this.props.editUserId && (this.props.userDetails.loading || this.props.userPermissions.loading))
                                ? (
                                    <div>
                                        <LoadingSpinner height="20" />
                                    </div>
                                ) : (
                                    <div>
                                        {this.props.editUserId && <p className="user-name">
                                            <i className="icons ion-ios-person-outline accent-text2"></i>
                                            {this.props.userDetails.data.firstName} {this.props.userDetails.data.lastName}
                                        </p>}
                                        <div className="modal-section">
                                            <label className="input-label section-label">Profile Settings</label>
                                            <div className="modal-row">
                                                <div className="column-half">
                                                    <ValidatedInput input={<input type="text" />}
                                                        label="First Name" name="firstName" value={this.state.userDetails.firstName} onChange={this.onChange}
                                                        required={true} showRequiredError={this.state.submitAttempted} />
                                                </div>
                                                <div className="column-half">
                                                    <ValidatedInput input={<input type="text" />}
                                                        label="Last Name" name="lastName" value={this.state.userDetails.lastName} onChange={this.onChange}
                                                        required={true} showRequiredError={this.state.submitAttempted} />
                                                </div>
                                            </div>
                                            <div className="modal-row">
                                                <div className="column-half">
                                                    <ValidatedPhoneInput
                                                        phoneNumber={this.state.userDetails.phoneNumber || ''}
                                                        phoneType={this.state.userDetails.phoneType || PhoneNumberTypes.American}
                                                        phoneNumberName="phoneNumber"
                                                        phoneTypeName="phoneType"
                                                        onChange={this.onChange}
                                                        required={true}
                                                        showRequiredError={this.state.submitAttempted}
                                                        error={this.state.invalidData.phoneNumber}
                                                        updateError={this.validatePhone.bind(this)}
                                                        errorName="phoneNumber" />
                                                </div>
                                                <div className="column-half">
                                                    <ValidatedPhoneInput label="Mobile Phone"
                                                        phoneNumber={this.state.userDetails.mobilePhone || ''}
                                                        phoneType={this.state.userDetails.mobilePhoneType || PhoneNumberTypes.American}
                                                        phoneNumberName="mobilePhone"
                                                        phoneTypeName="mobilePhoneType"
                                                        onChange={this.onChange}
                                                        required={true}
                                                        showRequiredError={this.state.submitAttempted}
                                                        error={this.state.invalidData.mobilePhone}
                                                        updateError={this.validatePhone.bind(this)}
                                                        errorName="mobilePhone" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-section">
                                            <label className="input-label section-label">Account Settings</label>
                                            <div className="modal-row">
                                                <div className="column-half">
                                                    <ValidatedInput input={<input type="text" />}
                                                        label="Email" name="email" value={this.state.userDetails.email} onChange={this.onChange}
                                                        required={true} showRequiredError={this.state.submitAttempted}
                                                        validated={true} showValidationError={true} validate={this.validateEmail.bind(this)}
                                                        errorMessage="Invalid Email Address" />
                                                </div>
                                                <div className="column-half">
                                                    <RadioGroup title="Department" name="departments"
                                                        value={this.state.userDetails.departments.sort().join(',')}
                                                        options={[...this.props.departments,
                                                        { key: this.props.departments.map(d => d.value).join(','), value: this.props.departments.map(d => d.value).sort().join(' & ') }]}
                                                        onChange={(name, val) => this.onChange(name, val.split(','))} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-section">
                                            <div className="modal-row">
                                                <div className="column-half">
                                                    <ValidatedInput input={<Select options={this.props.approvers.filter(a => this.state.userDetails.departments.length > 1 || a.departments.length > 1 || a.departments.indexOf(this.state.userDetails.departments[0]) === 0)} />}
                                                        label="Default Approver" name="defaultApproverId" value={this.state.userDetails.defaultApproverId}
                                                        onChange={this.onChange} required={true} showRequiredError={this.state.submitAttempted} />
                                                </div>
                                                <div className="column-half" />
                                            </div>
                                        </div>
                                        <div className="modal-section">
                                            <div className="modal-row">
                                                <div className="column-half role-settings">
                                                    <ValidatedInput input={<Select options={this.props.roles} />}
                                                        label="Role/Permissions" name="roleId" value={this.state.userDetails.roleId}
                                                        onChange={this.onChange} required={true} showRequiredError={this.state.submitAttempted} />
                                                    <FadeOutErrorMessage show={!this.props.permissionsForRole.loading && !this.props.permissionsForRole.success}
                                                        message="Unable to retrieve role permissions.  Please try again." />
                                                </div>
                                                <div className="column-half">
                                                    {this.props.permissionsForRole.loading
                                                        ? <div className="spinner-left"><label className="empty-label"></label><LoadingSpinner height="16" /></div>
                                                        : ((this.state.userDetails.roleId && this.props.permissionsForRole.success && !deepEquals(this.state.additionalPermissions, this.getDefaultAdditionalPermissions(), true))
                                                            && <button className="btn btn-reset" onClick={this.onResetPermissions}>
                                                                <i className="icon ion-ios-loop"></i> Reset
                                                        </button>)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-section permissions">
                                            <RadioGroup
                                                name="recipientTypePermission"
                                                title="Recipient Type"
                                                onChange={(name, value) =>
                                                    this.onToggleRecipientTypePermission(value)
                                                }
                                                options={[
                                                    ...Object.keys(this.recipientTypePermissions).map(
                                                        typePermission => ({
                                                            key: this.recipientTypePermissions[typePermission],
                                                            value: typePermission
                                                        })
                                                    ),
                                                    {
                                                        key: "all",
                                                        value: Object.keys(
                                                            this.recipientTypePermissions
                                                        ).join(" and ")
                                                    }
                                                ]}
                                                value={
                                                    this.state.additionalPermissions.indexOf(
                                                        this.recipientTypePermissions.Institutions
                                                    ) >= 0 &&
                                                        this.state.additionalPermissions.indexOf(
                                                            this.recipientTypePermissions.Individuals
                                                        ) >= 0
                                                        ? "all"
                                                        : this.state.additionalPermissions.indexOf(
                                                            this.recipientTypePermissions.Institutions
                                                        ) >= 0
                                                            ? this.recipientTypePermissions.Institutions
                                                            : this.state.additionalPermissions.indexOf(
                                                                this.recipientTypePermissions.Individuals
                                                            ) >= 0
                                                                ? this.recipientTypePermissions.Individuals
                                                                : ""
                                                }
                                            />
                                            <RadioGroup
                                                name="limitedFunds"
                                                title="Funds"
                                                onChange={(name, value) =>
                                                    this.onToggleFundsPermission(value)
                                                }
                                                options={[
                                                    {
                                                        key: PermissionTypes.AllFundsAccess,
                                                        value: "All"
                                                    },
                                                    {
                                                        key: PermissionTypes.LimitedFundsAccess,
                                                        value: "Limited"
                                                    }
                                                ]}
                                                value={
                                                    this.state.additionalPermissions.indexOf(
                                                        PermissionTypes.AllFundsAccess
                                                    ) >= 0
                                                        ? PermissionTypes.AllFundsAccess
                                                        : PermissionTypes.LimitedFundsAccess
                                                }
                                            />
                                            {this.state.additionalPermissions.indexOf(
                                                PermissionTypes.LimitedFundsAccess
                                            ) >= 0 && (
                                                    <div>
                                                        <MultiSelect
                                                            name="limitedFunds"
                                                            onChange={(name, value) =>
                                                                this.onSelectLimitedFunds(value, accessibleFunds)
                                                            }
                                                            options={[
                                                                ...(this.props.allowAllFundsAccess &&
                                                                    this.state.userDetails.limitedFunds.length <
                                                                    accessibleFunds.length
                                                                    ? [
                                                                        {
                                                                            label: "Select All",
                                                                            value: "all"
                                                                        }
                                                                    ]
                                                                    : []),
                                                                ...accessibleFunds
                                                            ]}
                                                            placeholder="Select Funds..."
                                                            value={
                                                                this.props.allowAllFundsAccess
                                                                    ? this.state.userDetails.limitedFunds
                                                                    : accessibleFunds
                                                                        .map(fund => fund.value)
                                                                        .filter(
                                                                            fundId =>
                                                                                this.state.userDetails.limitedFunds.indexOf(
                                                                                    fundId
                                                                                ) >= 0
                                                                        )
                                                            }
                                                        />
                                                        {!this.props.allowAllFundsAccess && (
                                                            <span className="xs-text error-text mt-16">
                                                                You do not have access to all funds. Please use
                                                                caution when updating funds for other users as you
                                                                may be blocking them from previously accessible
                                                                funds that are hidden from you.
                                                </span>
                                                        )}
                                                    </div>
                                                )}
                                            {Object.keys(permissionsByCategory).map(category => (
                                                <div key={category} className="flex-center">
                                                    <p className="accent-text small-text">{category}:</p>
                                                    <div className="permission-checks-container">
                                                        {permissionsByCategory[category].map(permission => (
                                                            <div
                                                                key={permission.key}
                                                                className="custom-check-container"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    id={permission.key}
                                                                    className="custom-check"
                                                                    name={permission.key}
                                                                    checked={
                                                                        this.props.permissionsForRole.data.indexOf(
                                                                            permission.key
                                                                        ) >= 0 ||
                                                                        this.state.additionalPermissions.indexOf(
                                                                            permission.key
                                                                        ) >= 0
                                                                    }
                                                                    disabled={
                                                                        this.props.permissionsForRole.data.indexOf(
                                                                            permission.key
                                                                        ) >= 0
                                                                    }
                                                                    onChange={this.onTogglePermission}
                                                                />
                                                                <label htmlFor={permission.key}>
                                                                    {permission.displayValue}{" "}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {this.props.creatingUser.loading ||
                                            this.props.updatingUser.loading ? (
                                                <LoadingSpinner height="20" alignRight={true} />
                                            ) : (
                                                <div className="modal-btns text-right">
                                                    <button
                                                        className="btn btn-accent"
                                                        onClick={this.onClose.bind(this)}
                                                    >
                                                        Cancel
                                            </button>
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => this.onSave()}
                                                        disabled={
                                                            this.state.invalidData.email ||
                                                            this.state.invalidData.phoneNumber ||
                                                            this.state.invalidData.mobilePhone ||
                                                            (deepEquals(
                                                                this.state.userDetails,
                                                                this.state.preEditUserDetails
                                                            ) &&
                                                                deepEquals(
                                                                    this.state.additionalPermissions,
                                                                    this.state.preEditAdditionalPermissions
                                                                ))
                                                        }
                                                    >
                                                        Save
                                            </button>
                                                    <FadeOutErrorMessage
                                                        show={this.state.error}
                                                        message={
                                                            this.props.editUserId
                                                                ? this.props.updatingUser.message
                                                                : this.props.creatingUser.message
                                                        }
                                                    />
                                                </div>
                                            )}
                                    </div>
                                )}
                        </div>
                    </div>
                </Modal>

                <ApproverReplacementModal
                    show={this.state.showModals.approverReplacementModal}
                    close={approverId => {
                        if (approverId)
                            this.setState({ replacementApproverId: approverId }, this.onSave.bind(this, true));
                        this.toggleModal("approverReplacementModal");
                    }}
                    user={{ name: `${this.state.preEditUserDetails.firstName} ${this.state.preEditUserDetails.lastName}`, userId: this.props.editUserId }}
                    approvers={this.props.approvers}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/settingComponents/userSettings/UserSettingsModal.jsx